import { useTranslation } from 'react-i18next';

interface EditorToolsSectionProps {
  readonly showNodeNumbers: boolean;
  readonly onToggleShowNodeNumbers: (value: boolean) => void;
  readonly showStereoLabels: boolean;
  readonly onToggleShowStereoLabels: (value: boolean) => void;
  /** #777 (owner): draws or folds the hydrogens -- unlike the others, it changes the drawing. */
  readonly showHydrogens?: boolean;
  readonly onToggleShowHydrogens?: (value: boolean) => void;
  /** #736 step 2: groups drawn as their labels (CF₃) -- like Show Hydrogens, it changes the drawing. */
  readonly groupsAsLabels?: boolean;
  readonly onToggleGroupsAsLabels?: (value: boolean) => void;
  readonly showBondAcceptors: boolean;
  readonly onToggleShowBondAcceptors: (value: boolean) => void;
  readonly showCenterOfRotation: boolean;
  readonly onToggleShowCenterOfRotation: (value: boolean) => void;
  readonly lassoSelectionEnabled: boolean;
  readonly onToggleLassoSelectionEnabled: (value: boolean) => void;
}

// The reference Edit tab's persistent "Tools" section (#561/#575/#576/#577/#578) -- Show Node
// Numbers, Show Bond Acceptors, Center of Rotation, Lasso Selection, Show R/S Labels (#1047), and
// Show Hydrogens (#777, which changes the drawing: one Undo step each way).
// Session-only UI state, not persisted server-side.
export function EditorToolsSection({
  showNodeNumbers,
  onToggleShowNodeNumbers,
  showStereoLabels,
  onToggleShowStereoLabels,
  showHydrogens = false,
  onToggleShowHydrogens,
  groupsAsLabels = false,
  onToggleGroupsAsLabels,
  showBondAcceptors,
  onToggleShowBondAcceptors,
  showCenterOfRotation,
  onToggleShowCenterOfRotation,
  lassoSelectionEnabled,
  onToggleLassoSelectionEnabled
}: EditorToolsSectionProps) {
  const { t } = useTranslation();
  return (
    <fieldset className="editor-tools-section">
      <legend>{t('editorToolsSection.legend')}</legend>
      {(
        [
          ['showCenterOfRotation', showCenterOfRotation, onToggleShowCenterOfRotation],
          ['lassoSelection', lassoSelectionEnabled, onToggleLassoSelectionEnabled],
          ['showNodeNumbers', showNodeNumbers, onToggleShowNodeNumbers],
          ['showStereoLabels', showStereoLabels, onToggleShowStereoLabels],
          ...(onToggleShowHydrogens ? ([['showHydrogens', showHydrogens, onToggleShowHydrogens]] as const) : []),
          ...(onToggleGroupsAsLabels ? ([['groupsAsLabels', groupsAsLabels, onToggleGroupsAsLabels]] as const) : []),
          ['showBondAcceptors', showBondAcceptors, onToggleShowBondAcceptors]
        ] as const
      ).map(([key, checked, onChange]) => (
        <label key={key}>
          <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
          {t(`editorToolsSection.${key}`)}
        </label>
      ))}
    </fieldset>
  );
}
